import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AllOpenOrdersPage.css";

const LIMIT = 50;

const FuturesPositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [pairId, setPairId] = useState("");
  const [positionId, setPositionId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("");
  const [status, setStatus] = useState("");
  const [liquidated, setLiquidated] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [pairId, positionId, symbol, side, status, liquidated, debouncedUser]);

  const buildParams = useCallback(() => {
    const params = { page, limit: LIMIT };
    if (pairId.trim()) params.pair_id = pairId.trim();
    if (positionId.trim()) params.position_id = positionId.trim();
    if (symbol.trim()) params.symbol = symbol.trim();
    if (side) params.side = side;
    if (status === "all") params.status = "all";
    else if (status) params.status = status;
    if (liquidated === "true" || liquidated === "false") params.liquidated = liquidated;
    if (debouncedUser) params.user = debouncedUser;
    return params;
  }, [page, pairId, positionId, symbol, side, status, liquidated, debouncedUser]);

  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getGlobalFuturesPositions(buildParams());
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : null;
        if (list) {
          setPositions(list);
          setTotalPages(res?.pagination?.total_pages || 1);
        } else {
          setPositions([]);
          setTotalPages(1);
          setError(res?.message || "Failed to load futures positions.");
        }
      } catch (e) {
        console.error(e);
        setPositions([]);
        setError("Failed to load futures positions.");
      } finally {
        setLoading(false);
      }
    };
    fetchPositions();
  }, [buildParams]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 10 })
      : "—";

  const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

  const pairLabel = (row) =>
    row.pair?.name || row.pair?.short_name || row.symbol || "—";

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trading</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Positions</span>
          </p>
          <h1 className="dashboard-hero__title">Futures Positions</h1>
          <p className="dashboard-hero__subtitle">
            Global futures positions (API filters; default OPEN)
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-layer-group" />
              </span>
              <h2 className="openorders-section__title">Futures Positions</h2>
            </div>
            <div className="openorders-section__stats">
              <span className="openorders-stat">
                <strong>{positions.length}</strong> on page
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 200px" }}
            />
            <input
              type="text"
              placeholder="Symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 140px" }}
            />
            <input
              type="text"
              placeholder="Pair ID (ObjectId)"
              value={pairId}
              onChange={(e) => setPairId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Position ID (ObjectId)"
              value={positionId}
              onChange={(e) => setPositionId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 200px" }}
            />
            <select
              value={side}
              onChange={(e) => setSide(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Side (any)</option>
              <option value="LONG">LONG</option>
              <option value="SHORT">SHORT</option>
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 180px" }}
            >
              <option value="">Status (OPEN default)</option>
              <option value="all">all</option>
              <option value="OPEN">OPEN</option>
              <option value="CLOSED">CLOSED</option>
              <option value="HOLD">HOLD</option>
            </select>
            <select
              value={liquidated}
              onChange={(e) => setLiquidated(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Liquidated</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : positions.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>Position ID</th>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Pair</th>
                      <th>Side</th>
                      <th>Qty</th>
                      <th>Entry</th>
                      <th>Mark</th>
                      <th>Liq. price</th>
                      <th>uPnL</th>
                      <th>rPnL</th>
                      <th>Isolated</th>
                      <th>Maint.</th>
                      <th>Lev</th>
                      <th>Liq.</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((row) => {
                      const docId = row.id ?? row._id ?? row.position_id;
                      const uid = row.user?.id || row.user_id;
                      const u = row.user;
                      const userUuidDisplay = u?.uuid ?? "—";
                      const nameStr = userFullName(u);
                      const emailStr = u?.email ?? "—";
                      const phoneStr = u?.phone ?? "—";
                      const p = pairLabel(row);
                      const qty =
                        row.quantity ?? row.size ?? row.qty;
                      const entry =
                        row.entryPrice ?? row.entry_price ?? row.avg_entry;
                      const mark = row.markPrice ?? row.mark_price;
                      const liqPrice =
                        row.liquidationPrice ?? row.liquidation_price;
                      const uPnl =
                        row.unrealizedPnl ?? row.unrealized_pnl;
                      const rPnl =
                        row.realizedPnl ?? row.realized_pnl;
                      const isolated =
                        row.isolatedMargin ??
                        row.isolated_margin ??
                        row.margin ??
                        row.initial_margin;
                      const maint =
                        row.maintenanceMargin ?? row.maintenance_margin;
                      const lev = row.leverage;
                      const wasLiq = row.liquidated;
                      const updatedAt = row.updatedAt ?? row.updated_at;
                      return (
                        <tr key={docId}>
                          <td>
                            <span
                              className="openorders-order-id"
                              title={docId ? `Position id: ${docId}` : undefined}
                            >
                              {docId ?? "—"}
                            </span>
                          </td>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{ color: "#dc2626", fontWeight: 600, textDecoration: "none" }}
                              >
                                {userUuidDisplay}
                              </Link>
                            ) : (
                              userUuidDisplay
                            )}
                          </td>
                          <td>{nameStr}</td>
                          <td>{emailStr}</td>
                          <td>{phoneStr}</td>
                          <td>
                            <strong title={row.pair?.margin_asset ? `Margin: ${row.pair.margin_asset}` : ""}>
                              {p}
                            </strong>
                          </td>
                          <td>
                            <span className={`openorders-side openorders-side--${(row.side || "").toLowerCase()}`}>
                              {row.side ?? "—"}
                            </span>
                          </td>
                          <td>{formatNum(qty)}</td>
                          <td>{formatNum(entry)}</td>
                          <td>{formatNum(mark)}</td>
                          <td>{formatNum(liqPrice)}</td>
                          <td>{formatNum(uPnl)}</td>
                          <td>{formatNum(rPnl)}</td>
                          <td>{formatNum(isolated)}</td>
                          <td>{formatNum(maint)}</td>
                          <td>{lev != null ? `${lev}×` : "—"}</td>
                          <td>{yn(wasLiq)}</td>
                          <td>{row.status ?? "—"}</td>
                          <td
                            title={
                              row.createdAt
                                ? `Opened: ${formatDate(row.createdAt)}`
                                : undefined
                            }
                          >
                            {formatDate(updatedAt || row.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: "0.75rem 0", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No futures positions match your filters.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default FuturesPositionsPage;
